// src/pages/Moviles.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

// NextUI 2.x
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Input,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from "@nextui-org/react";

// Iconos
import { IconSearch } from "@tabler/icons-react";

import "../styles/Moviles.css";

function Moviles() {
  /* ────────  STATE  ──────── */
  const [movil, setMovil] = useState("");
  const [numeroTelefono, setNumero] = useState("");
  const [chatIdTelegram, setChatId] = useState("");
  const [contactos, setContactos] = useState([]);
  const [editIndex, setEditIndex] = useState(null);

  // UI/UX
  const [query, setQuery] = useState("");

  /* ────────  FETCH  ──────── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          "https://pedidos.ayvgas.cl/pedidos/contactos.json"
        );
        setContactos(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error al obtener contactos:", err);
      }
    })();
  }, []);

  /* ────────  DERIVED (items con key para NextUI)  ──────── */
  const items = useMemo(
    () =>
      contactos.map((c, i) => ({
        ...c,
        key: String(c.id ?? c.chat_id_telegram ?? c.numero_telefono ?? i),
        __idx: i, // índice original para editar/borrar
      })),
    [contactos]
  );

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      [it.movil, it.numero_telefono, it.chat_id_telegram]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [items, query]);

  /* ────────  HELPERS  ──────── */
  const resetForm = () => {
    setMovil("");
    setNumero("");
    setChatId("");
    setEditIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      movil,
      numero_telefono: numeroTelefono,
      chat_id_telegram: chatIdTelegram,
    };

    try {
      if (editIndex !== null) {
        await axios.post(
          "https://pedidos.ayvgas.cl/pedidos/editar_contacto.php",
          { index: editIndex, ...payload }
        );
        setContactos((prev) =>
          prev.map((c, i) => (i === editIndex ? payload : c))
        );
      } else {
        await axios.post(
          "https://pedidos.ayvgas.cl/pedidos/insertar_contacto.php",
          payload
        );
        setContactos((prev) => [...prev, payload]);
      }
    } catch (err) {
      console.error("Error guardando contacto:", err);
      alert("Hubo un error al guardar el contacto");
    } finally {
      resetForm();
    }
  };

  const handleEdit = (idx) => {
    const c = contactos[idx];
    if (!c) return;
    setMovil(c.movil || "");
    setNumero(c.numero_telefono || "");
    setChatId(c.chat_id_telegram || "");
    setEditIndex(idx);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (idx) => {
    if (!window.confirm("¿Eliminar este contacto?")) return;
    try {
      await axios.post(
        "https://pedidos.ayvgas.cl/pedidos/eliminar_contacto.php",
        { index: idx }
      );
      setContactos((prev) => prev.filter((_, i) => i !== idx));
    } catch (err) {
      console.error("Error eliminando contacto:", err);
      alert("Hubo un error al eliminar el contacto");
    }
  };

  /* ────────  UI  ──────── */
  return (
    <section className="moviles">
      {/* HEADER */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Móviles & Contacto</h1>
          <p className="page-subtitle">
            Gestiona los números y Chat IDs de Telegram de tu operación.
          </p>
        </div>
      </div>

      {/* GRID: FORM + TABLE */}
      <div className="grid">
        {/* FORM */}
        <Card className="card-dark form-card elevated">
          <CardHeader className="card-header">
            <h2 className="card-title">
              {editIndex !== null ? "Editar contacto" : "Agregar nuevo móvil"}
            </h2>
          </CardHeader>
          <CardBody>
            <form onSubmit={handleSubmit} className="form" noValidate>
              <Input
                className="nx-input"
                label="Móvil"
                variant="bordered"
                radius="lg"
                size="md"
                value={movil}
                onValueChange={setMovil}
                required
                placeholder="Ej: M36"
              />
              <Input
                className="nx-input"
                label="Número de teléfono"
                variant="bordered"
                radius="lg"
                size="md"
                type="tel"
                value={numeroTelefono}
                onValueChange={setNumero}
                required
                placeholder="+56 9 1234 5678"
              />
              <Input
                className="nx-input"
                label="Chat ID de Telegram"
                variant="bordered"
                radius="lg"
                size="md"
                value={chatIdTelegram}
                onValueChange={setChatId}
                required
                placeholder="123456789"
              />
              <div className="form-actions">
                <Button type="submit" color="success" radius="sm">
                  {editIndex !== null ? "Guardar cambios" : "Agregar"}
                </Button>
                {editIndex !== null && (
                  <Button
                    type="button"
                    color="default"
                    variant="flat"
                    radius="sm"
                    onClick={resetForm}
                  >
                    Cancelar
                  </Button>
                )}
              </div>
            </form>
          </CardBody>
        </Card>

        {/* TABLE */}
        <Card className="card-dark table-card elevated">
          <CardHeader className="card-header">
            <div className="table-header">
              <h3 className="card-title">Lista de contactos</h3>
              <Input
                className="search nx-search"
                placeholder="Buscar por móvil, teléfono o Chat ID…"
                aria-label="Buscar por móvil, teléfono o Chat ID…"
                variant="bordered"
                radius="lg"
                size="lg"
                isClearable
                value={query}
                onValueChange={setQuery}
                startContent={<IconSearch size={18} />}
              />
            </div>
          </CardHeader>

          <CardBody>
            <div className="table-scroll">
              <Table
                aria-label="Tabla de contactos"
                className="dark-table modern"
                removeWrapper
              >
                <TableHeader>
                  <TableColumn>Móvil</TableColumn>
                  <TableColumn>Teléfono</TableColumn>
                  <TableColumn>Chat ID Telegram</TableColumn>
                  <TableColumn className="col-acciones">Acciones</TableColumn>
                </TableHeader>

                <TableBody
                  emptyContent="Sin contactos registrados"
                  items={filteredItems}
                >
                  {(item) => (
                    <TableRow key={item.key}>
                      <TableCell>{item.movil}</TableCell>
                      <TableCell>{item.numero_telefono}</TableCell>
                      <TableCell>{item.chat_id_telegram}</TableCell>
                      <TableCell>
                        <div className="acciones">
                          <Button
                            size="sm"
                            color="warning"
                            variant="solid"
                            radius="sm"
                            onClick={() => handleEdit(item.__idx)}
                          >
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            color="danger"
                            variant="solid"
                            radius="sm"
                            onClick={() => handleDelete(item.__idx)}
                          >
                            Borrar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </div>
    </section>
  );
}

export default Moviles;
